<template>
    <transition name="download-queue-progress">
        <aside v-if="hasItems" class="floating-download-queue" role="status" aria-live="polite">
            <section v-if="isExpanded" class="floating-download-queue__panel">
                <header class="floating-download-queue__panel-header">
                    <h3>Downloads</h3>

                    <div class="floating-download-queue__panel-actions">
                        <button
                            class="floating-download-queue__dismiss-text-button"
                            type="button"
                            :aria-label="primaryActionLabel"
                            @click="handlePrimaryAction"
                        >
                            {{ primaryActionLabel }}
                        </button>
                    </div>
                </header>

                <div v-if="currentDownload" class="floating-download-queue__file-row">
                    <div class="floating-download-queue__file-icon is-downloading">
                        <font-awesome-icon icon="fa-solid fa-arrow-down" />
                    </div>

                    <div class="floating-download-queue__file-content">
                        <div class="floating-download-queue__file-heading">
                            <strong class="text-truncate">{{ currentDownload.name }}</strong>
                            <button
                                class="floating-download-queue__icon-button"
                                type="button"
                                aria-label="Cancel download"
                                @click="downloadQueue.cancelDownload(currentDownload)"
                            >
                                <font-awesome-icon icon="fa-solid fa-times" />
                            </button>
                        </div>

                        <div class="floating-download-queue__file-meta">
                            <span>{{ currentDownload.progress || 0 }}% of {{ formatFileSize(currentDownload.size) }}</span>
                        </div>

                        <v-progress-linear
                            :model-value="currentDownload.progress"
                            :indeterminate="!currentDownload.progress"
                            bg-color="#E5E7EB"
                            color="#FF6C37"
                            height="6"
                        />
                    </div>
                </div>

                <div v-if="requests.length" class="floating-download-queue__file-list">
                    <div class="floating-download-queue__file-list-header">
                        <span>Render queue</span>
                        <span>{{ requests.length }}</span>
                    </div>

                    <div
                        v-for="item in requests"
                        :key="`render-${item.eventId}`"
                        class="floating-download-queue__file-item"
                        :class="{ 'floating-download-queue__file-item--active': item.status === 'rendering' }"
                    >
                        <span class="floating-download-queue__file-status" :class="statusClassForRequest(item)">
                            <font-awesome-icon :icon="statusIconForRequest(item)" :spin="item.status === 'rendering'" />
                        </span>

                        <div class="floating-download-queue__file-item-copy">
                            <strong class="text-truncate">{{ item.downloadName }}</strong>
                            <small v-if="item.error" class="floating-download-queue__file-item-error">{{ item.error }}</small>
                            <small v-else>{{ statusLabelForRequest(item) }}</small>
                        </div>

                        <button
                            class="floating-download-queue__icon-button floating-download-queue__icon-button--small"
                            type="button"
                            aria-label="Remove render request"
                            @click="mutedDownloadQueue.removeRequest(item.eventId)"
                        >
                            <font-awesome-icon icon="fa-solid fa-times" />
                        </button>
                    </div>
                </div>

                <div class="floating-download-queue__file-list">
                    <div class="floating-download-queue__file-list-header">
                        <span>Queue</span>
                        <span>{{ downloadQueue.finishedDownloads.length }} / {{ downloadQueue.downloads.length + requests.length }}</span>
                    </div>

                    <div
                        v-for="item in downloadQueue.downloads"
                        :key="item.id"
                        class="floating-download-queue__file-item"
                        :class="{ 'floating-download-queue__file-item--active': item.id === currentDownload?.id }"
                    >
                        <span class="floating-download-queue__file-status" :class="statusClassFor(item)">
                            <font-awesome-icon :icon="statusIconFor(item)" />
                        </span>

                        <div class="floating-download-queue__file-item-copy">
                            <strong class="text-truncate">{{ item.name }}</strong>
                            <small v-if="item.error" class="floating-download-queue__file-item-error">{{ item.error }}</small>
                            <small v-else>{{ statusLabelFor(item) }}</small>
                        </div>

                        <button
                            v-if="item.id !== currentDownload?.id"
                            class="floating-download-queue__icon-button floating-download-queue__icon-button--small"
                            type="button"
                            aria-label="Remove from queue"
                            @click="downloadQueue.cancelDownload(item)"
                        >
                            <font-awesome-icon icon="fa-solid fa-times" />
                        </button>
                    </div>
                </div>

                <footer class="floating-download-queue__panel-footer">
                    <p class="floating-download-queue__footer-note">{{ footerNote }}</p>

                    <div class="floating-download-queue__footer-actions">
                        <button
                            class="floating-download-queue__icon-button"
                            type="button"
                            aria-label="Collapse download details"
                            title="Collapse"
                            @click="isExpanded = false"
                        >
                            <font-awesome-icon :icon="isExpanded ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" />
                        </button>
                    </div>
                </footer>
            </section>

            <div
                v-else
                class="floating-download-queue__dock"
                role="button"
                tabindex="0"
                @click="isExpanded = true"
                @keydown.enter="isExpanded = true"
                @keydown.space.prevent="isExpanded = true"
            >
                <button
                    v-if="!isDownloading"
                    class="floating-download-queue__icon-button floating-download-queue__icon-button--small floating-download-queue__dock-close"
                    type="button"
                    aria-label="Dismiss and clear queue"
                    title="Dismiss"
                    @click.stop="handlePrimaryAction()"
                >
                    <font-awesome-icon icon="fa-solid fa-times" />
                </button>

                <span class="floating-download-queue__dock-icon" :class="dockStatusClass">
                    <font-awesome-icon :icon="dockStatusIcon" />
                </span>

                <span class="floating-download-queue__dock-copy">
                    <strong>{{ compactTitle }}</strong>
                    <small>{{ compactSubtitle }}</small>
                </span>

                <span class="floating-download-queue__dock-chevron">
                    <font-awesome-icon icon="fa-solid fa-chevron-up" />
                </span>
            </div>
        </aside>
    </transition>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useDownloadQueue, type Download } from '@/stores/downloadQueue';
import { useMutedDownloadQueue, type MutedDownloadRequest } from '@/stores/mutedDownloadQueue';
import { formatFileSize } from '@/plugins/filters';

const downloadQueue = useDownloadQueue();
const mutedDownloadQueue = useMutedDownloadQueue();
const { downloads, activeDownloads, finishedDownloads, failedDownloads, isDownloading } = storeToRefs(downloadQueue);
const { requests, renderingRequests, erroredRequests } = storeToRefs(mutedDownloadQueue);

const isExpanded = ref(false);
const hasItems = computed(() => downloads.value.length > 0 || requests.value.length > 0);
const currentDownload = computed<Download | undefined>(() => activeDownloads.value[0]);

const primaryActionLabel = computed(() => (isDownloading.value || requests.value.length > 0 ? 'Cancel All' : 'Clear'));
function handlePrimaryAction() {
    downloadQueue.cancelAllDownloads();
    mutedDownloadQueue.clearRequests();
}

function statusOf(item: Download): 'downloading' | 'complete' | 'error' | 'queued' {
    if (item.error) return 'error';
    if (item.completed) return 'complete';
    if (item.id === currentDownload.value?.id) return 'downloading';
    return 'queued';
}
function statusClassFor(item: Download) {
    return `is-${statusOf(item)}`;
}
function statusIconFor(item: Download) {
    switch (statusOf(item)) {
        case 'complete':
            return 'fa-solid fa-check';
        case 'error':
            return 'fa-solid fa-triangle-exclamation';
        case 'downloading':
            return 'fa-solid fa-arrow-down';
        default:
            return 'fa-solid fa-clock';
    }
}
function statusLabelFor(item: Download) {
    switch (statusOf(item)) {
        case 'complete':
            return 'Downloaded';
        case 'downloading':
            return `${item.progress || 0}%`;
        default:
            return 'Queued';
    }
}

function statusOfRequest(item: MutedDownloadRequest): 'downloading' | 'error' {
    return item.status === 'error' ? 'error' : 'downloading';
}

function statusClassForRequest(item: MutedDownloadRequest) {
    return `is-${statusOfRequest(item)}`;
}

function statusIconForRequest(item: MutedDownloadRequest) {
    return item.status === 'error' ? 'fa-solid fa-triangle-exclamation' : 'fa-solid fa-spinner';
}

function statusLabelForRequest(item: MutedDownloadRequest) {
    return item.status === 'error' ? item.error || 'Render failed' : 'Processing render';
}

const dockStatusClass = computed(() => {
    if (isDownloading.value) return 'is-downloading';
    if (renderingRequests.value.length) return 'is-downloading';
    if (erroredRequests.value.length) return 'is-error';
    if (failedDownloads.value.length) return 'is-error';
    return 'is-complete';
});
const dockStatusIcon = computed(() => {
    if (isDownloading.value) return 'fa-solid fa-arrow-down';
    if (renderingRequests.value.length) return 'fa-solid fa-spinner';
    if (erroredRequests.value.length) return 'fa-solid fa-triangle-exclamation';
    if (failedDownloads.value.length) return 'fa-solid fa-triangle-exclamation';
    return 'fa-solid fa-check';
});

const compactTitle = computed(() => {
    if (isDownloading.value) {
        return activeDownloads.value.length > 1 ? `Downloading ${activeDownloads.value.length} videos` : 'Downloading video';
    }
    if (renderingRequests.value.length) {
        return renderingRequests.value.length > 1 ? `Processing ${renderingRequests.value.length} renders` : 'Processing render';
    }
    if (erroredRequests.value.length) {
        return `${erroredRequests.value.length} render${erroredRequests.value.length > 1 ? 's' : ''} failed`;
    }
    if (failedDownloads.value.length) {
        return `${failedDownloads.value.length} download${failedDownloads.value.length > 1 ? 's' : ''} failed`;
    }
    return 'Downloads complete';
});
const compactSubtitle = computed(() => {
    if (isDownloading.value && currentDownload.value) {
        return `${currentDownload.value.name} — ${currentDownload.value.progress || 0}%`;
    }
    if (renderingRequests.value.length) {
        return 'The muted download will start automatically when rendering completes.';
    }
    if (erroredRequests.value.length) {
        return 'One or more muted renders failed. Remove the request or try again.';
    }
    return `${finishedDownloads.value.length} of ${downloads.value.length} complete`;
});
const footerNote = computed(() => {
    if (isDownloading.value) {
        return 'You can keep working while these downloads continue in the background.';
    }
    if (renderingRequests.value.length) {
        return 'Rendering is in progress. The download will begin automatically when it is ready.';
    }
    if (erroredRequests.value.length) {
        return 'Some render requests failed - remove them or try again.';
    }
    if (failedDownloads.value.length) {
        return 'Some downloads failed — remove them or try again.';
    }
    return 'All downloads are complete.';
});

const AUTO_DISMISS_DELAY_MS = 120_000;
let autoDismissTimeout: ReturnType<typeof setTimeout> | null = null;

function clearAutoDismissTimeout(): void {
    if (!autoDismissTimeout) return;
    clearTimeout(autoDismissTimeout);
    autoDismissTimeout = null;
}

watch(
    () => ({ idle: !isDownloading.value && requests.value.length === 0, count: downloads.value.length + requests.value.length }),
    state => {
        clearAutoDismissTimeout();
        if (!state.idle || state.count === 0) return;
        autoDismissTimeout = setTimeout(() => {
            downloadQueue.cancelAllDownloads();
            mutedDownloadQueue.clearRequests();
            autoDismissTimeout = null;
        }, AUTO_DISMISS_DELAY_MS);
    },
    { immediate: true },
);

onUnmounted(clearAutoDismissTimeout);
</script>

<style scoped lang="scss">
.floating-download-queue {
    position: fixed;
    left: 50%;
    top: 24px;
    transform: translateX(-50%);
    z-index: 1000;
    color: #241914;
}

.floating-download-queue__dock,
.floating-download-queue__panel {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.14);
}

.floating-download-queue__dock {
    position: relative;
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    align-items: center;
    width: min(442px, calc(100vw - 32px));
    min-height: 74px;
    padding: 8px 40px 8px 8px;
    border-radius: 16px;
    color: inherit;
    text-align: left;
    cursor: pointer;
}

.floating-download-queue__dock-icon,
.floating-download-queue__file-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ff6c37;
    color: white;
}

.floating-download-queue__dock-icon {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    font-size: 1.4rem;
}

.floating-download-queue__dock-copy {
    display: grid;
    gap: 2px;
    min-width: 0;
    padding: 0 12px;
}

.floating-download-queue__dock-copy strong,
.floating-download-queue__file-heading strong {
    overflow: hidden;
    font-size: 0.95rem;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.floating-download-queue__dock-copy small {
    overflow: hidden;
    color: #6b7280;
    font-size: 0.8rem;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.floating-download-queue__dock-chevron {
    position: absolute;
    right: 14px;
    bottom: 12px;
    color: #6b7280;
    font-size: 0.9rem;
}

.floating-download-queue__panel {
    width: min(440px, calc(100vw - 32px));
    padding: 18px;
    border-radius: 18px;
}

.floating-download-queue__panel-header,
.floating-download-queue__file-heading,
.floating-download-queue__panel-actions,
.floating-download-queue__footer-actions {
    display: flex;
    align-items: center;
}

.floating-download-queue__panel-header,
.floating-download-queue__file-heading {
    justify-content: space-between;
}

.floating-download-queue__panel-header h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
}

.floating-download-queue__panel-actions,
.floating-download-queue__footer-actions {
    gap: 8px;
}

.floating-download-queue__icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    border: 0;
    background: #fff1eb;
    color: #9f3d17;
    cursor: pointer;
    flex-shrink: 0;
}

.floating-download-queue__icon-button--small {
    width: 24px;
    height: 24px;
    font-size: 0.72rem;
}

.floating-download-queue__dock-close {
    position: absolute;
    top: 8px;
    right: 8px;
}

.floating-download-queue__dismiss-text-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 28px;
    padding: 0 12px;
    border: 1px solid rgba(0, 0, 0, 0.87);
    border-radius: 4px;
    background: #ffffff;
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.0892857143em;
    line-height: 1;
    text-transform: uppercase;
    cursor: pointer;
    transition:
        background-color 150ms ease,
        border-color 150ms ease,
        color 150ms ease;
}

.floating-download-queue__dismiss-text-button:hover {
    border-color: #ffbea5;
    background: #fff3ed;
    color: #9f3d17;
}

.floating-download-queue__file-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 12px;
    margin-top: 20px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #fafafa;
}

.floating-download-queue__file-icon {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    font-size: 0.78rem;
}

.floating-download-queue__file-content {
    display: grid;
    gap: 8px;
    min-width: 0;
}

.floating-download-queue__file-heading strong {
    color: #241914;
    font-size: 0.9rem;
}

.floating-download-queue__file-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 0.75rem;
}

.floating-download-queue__file-list {
    display: grid;
    gap: 8px;
    margin-top: 14px;
    max-height: 220px;
    overflow-y: auto;
}

.floating-download-queue__file-list-header,
.floating-download-queue__file-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
}

.floating-download-queue__file-list-header {
    color: #6b7280;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
}

.floating-download-queue__file-item {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    gap: 10px;
    padding: 9px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #ffffff;
}

.floating-download-queue__file-item--active {
    border-color: #ffbea5;
    background: #fff8f5;
}

.floating-download-queue__file-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #e5e7eb;
    color: #4b5563;
    font-size: 0.68rem;
}

.floating-download-queue__file-item-copy {
    display: grid;
    gap: 2px;
    min-width: 0;

    strong,
    small {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        color: #241914;
        font-size: 0.82rem;
        line-height: 1.15;
    }

    small {
        color: #6b7280;
        font-size: 0.72rem;
        line-height: 1.15;
    }
}

.floating-download-queue__file-item-error {
    color: #b3261e !important;
}

.floating-download-queue__panel-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 16px;
    margin-top: 18px;
}

.floating-download-queue__footer-note {
    min-width: 0;
    max-width: none;
    margin: 0;
    color: #6b7280;
    font-size: 0.78rem;
    line-height: 1.35;
}

.floating-download-queue__footer-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
    align-self: end;
    min-width: 0;
    margin-left: auto;
}

.is-complete {
    background: #21a67a;
}

.is-downloading {
    background: #ff6c37;
    color: white;
}

.is-queued {
    background: #e5e7eb;
    color: #4b5563;
}

.is-error {
    background: #d64545;
    color: white;
}

.download-queue-progress-enter-active,
.download-queue-progress-leave-active {
    transition: opacity 180ms ease;
}

.download-queue-progress-enter-from,
.download-queue-progress-leave-to {
    opacity: 0;
}

@media (max-width: 720px) {
    .floating-download-queue {
        right: 20%;
        left: 20%;
        top: 12px;
        transform: none;
        margin: 0 auto;
    }

    .floating-download-queue__dock,
    .floating-download-queue__panel {
        width: 100%;
        max-width: 60vw;
    }

    .floating-download-queue__panel-footer {
        grid-template-columns: 1fr;
        margin-top: 20px;
    }

    .floating-download-queue__footer-note {
        max-width: none;
    }

    .floating-download-queue__footer-actions {
        justify-content: flex-start;
        margin-left: 0;
    }
}
</style>
